import React, { useState, useCallback } from 'react';
import { Accordion } from '../../molecules/accordion/Accordion';
import GiftCardForm from '../../molecules/forms/giftCardForm';
import { Heading } from '../../typography/heading/Heading';
import { Text } from '../../typography/text/Text';
import { Title } from '../../typography/title/Title';
import ProductGallery from '../productGallery/ProductGallery';
import Breadcrumbs from '../../molecules/breadcrumbs/Breadcrumbs';
import styles from './GiftCardPage.module.scss';
import { options, customClassesObj, settingsSliderPerView } from './options';
import { Plus } from '../../atoms/icons';
import { optionsActions } from '../../templates/product/options';

export interface ProductImage {
  url: string;
}

export interface GalleryItem {
  id: string;
  url?: string;
  videoUrl?: string;
}

export interface ProductAttributes {
  productvideourl?: string;
  ['gift-card-details']?: string;
}

export interface ProductVariant {
  id: string | number;
  price: number;
  images: ProductImage[];
  attributes: ProductAttributes;
}

export interface Product {
  name: string;
  productId: string | number;
  variants: ProductVariant[];
}

export interface Breadcrumb {
  name: string;
  url: string;
}

export interface GiftCardPageProps {
  title: string;
  description: string;
  product: Product;
  breadcrumbs?: Breadcrumb[];
  handleAddToCart: (variant: ProductVariant) => void;
}

const GiftCardPage: React.FC<GiftCardPageProps> = ({
  title,
  description,
  product,
  handleAddToCart,
  breadcrumbs,
}) => {
  const { variants, productId } = product;
  const [activeVariant, setActiveVariant] = useState<ProductVariant>(product.variants[0]);
  const { attributes } = activeVariant;

  const getProductImages = useCallback((): GalleryItem[] => {
    const data: GalleryItem[] = [];

    if (attributes?.productvideourl) {
      data.push({
        id: attributes.productvideourl,
        videoUrl: attributes.productvideourl,
      });
    }

    if (activeVariant?.images) {
      activeVariant.images.forEach((image, index) => {
        data.push({
          id: `${image.url}_${index}`,
          url: image.url,
        });
      });
    }

    return data;
  }, [activeVariant, attributes]);

  const markup = attributes?.['gift-card-details'] || '';

  const formatVariants = variants
    .filter((el) => el.price)
    .sort((a, b) => a.price - b.price);

  return (
    <div className={styles.wrapper}>
      <ProductGallery
        /*@ts-ignore*/
        isDemo={true}
        images={getProductImages()}
        className={styles.giftGallery}
        alt={product.name}
        customClasses={customClassesObj}
        settingsSliderPerView={settingsSliderPerView}
      />

      <div className={styles.sideBlock}>
        {breadcrumbs && (
          <Breadcrumbs
            className={styles.breadcrumbs}
            crumbs={[{ name: 'Home', url: '/' }, ...breadcrumbs]}
          />
        )}

        <Title
          align="start"
          className={styles.title}
          customClasses={{ title: styles.title }}
        >
          <Heading className={styles.giftCardTitle} weight="light" as="h1">
            {title}
          </Heading>
          <Text className={styles.description}>{description}</Text>
        </Title>

        <GiftCardForm
          productId={productId}
          activeVariant={activeVariant}
          setActiveVariant={setActiveVariant}
          variants={formatVariants}
          handleAddToCart={handleAddToCart}
        />

        {attributes?.['gift-card-details'] && (
          <Accordion
            customClasses={{ accordionWrapper: styles.accordionWrapper }}
            className={styles.accordion}
            canOpenMultiple={false}
          >
            <Accordion.Section
              id="0"
              title="additional details"
              key="0"
              isOpen={true}
              className={styles.accordionSection}
            >
              <Accordion.Title
                customClasses={{ title: styles.accordionTitle }}
                isRotate={optionsActions.isJag}
                timeout={200}
                iconToOpen={!optionsActions.isJag && Plus}
              >
                <Text
                  className={styles.accordionLabel}
                  as="p"
                  textTransform="uppercase"
                  weight="semibold"
                >
                  Additional details
                </Text>
              </Accordion.Title>

              {/*@ts-ignore*/}
              <Accordion.Panel customClasses={options.customClasses}>
                <div
                  className={styles.dynamicContentBlock}
                  dangerouslySetInnerHTML={{
                    __html: markup
                      .replace(/<li\/>/g, '</li>')
                      .split('<li></li>')
                      .join('')
                      .split('<li> </li>')
                      .join(''),
                  }}
                />
              </Accordion.Panel>
            </Accordion.Section>
          </Accordion>
        )}
      </div>
    </div>
  );
};

export default GiftCardPage;
